<template>
  <div>
    <el-dialog
      append-to-body
      class="mall-recharge-dialog"
      :visible="show"
      :before-close="handleClose"
      :title="$t('financial.mall.rechargeTitle')"
      width="416px"
    >
      <el-form
        ref="rechargeForm"
        :model="rechargeForm"
        :rules="rechargeRules"
        class="horizontal"
        label-position="left"
        @submit.native.prevent
        label-width="96px"
      >
        <el-form-item :label="$t('financial.mall.accountBalance')" required>
          <span class="mall-recharge-dialog__balance">{{
            balance | filterToYuan
          }}</span>
        </el-form-item>
        <el-form-item :label="$t('financial.mall.rechargeAmountLabel')" prop="rechargeAmount">
          <el-input
            v-model="rechargeForm.rechargeAmount"
            :placeholder="$t('financial.mall.rechargeAmountPlaceholder')"
          >
            <i slot="prefix" style="margin-left: 5px">¥</i>
          </el-input>
        </el-form-item>
      </el-form>
      <div class="mall-recharge-dialog__button">
        <el-button
          type="primary"
          class="page-button_medium"
          :loading="rechargeLoading"
          @click="handleSubmit"
          >{{ $t('financial.payment.rechargeNow') }}</el-button
        >
      </div>
    </el-dialog>

    <el-dialog
      append-to-body
      class="mall-recharge-dialog__qrcode"
      :visible="showQrcode"
      :before-close="handleQrcodeClose"
      :close-on-click-modal="false"
      width="360px"
    >
      <div class="mall-recharge-dialog__qrcode-box">
        <p class="mall-recharge-dialog__qrcode-title">{{ $t('financial.mall.rechargeAmountLabel') }}</p>
        <p class="mall-recharge-dialog__qrcode-price">
          ¥{{ qrcodeData.totalFee | filterToYuan }}
        </p>
        <img
          class="mall-recharge-dialog__qrcode-image"
          :src="qrcodeData.codeImgUrl"
          :alt="$t('financial.mall.qrcodeAlt')"
        />
        <p class="mall-recharge-dialog__qrcode-desc">{{ $t('financial.payment.scanPay') }}</p>
        <p class="mall-recharge-dialog__qrcode-desc">{{ $t('financial.mall.scanPaySupport') }}</p>
        <p v-if="showNoPayMsg" class="mall-recharge-dialog__qrcode-tips">
          {{ $t('financial.mall.notRecharged') }}
        </p>
        <el-button
          type="primary"
          class="page-button_medium"
          :loading="checkLoading"
          @click="checkPayStatus"
          >{{ $t('financial.mall.iHaveRecharged') }}</el-button
        >
      </div>
    </el-dialog>

    <el-dialog
      append-to-body
      class="mall-recharge-dialog__result"
      :visible="showResult"
      :before-close="handleResultClose"
      :show-close="false"
      width="256px"
    >
      <div class="mall-recharge-dialog__result-box">
        <i class="el-icon-circle-check" v-if="isResultSucess"></i>
        <i class="el-icon-circle-close" v-else></i>
        <p>{{ resultMsg }}</p>
        <el-button type="primary" @click="handleResultClose">{{ $t('common.confirm') }}</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import * as financialApi from '@/apis/financial'

export default {
  name: 'accountRechargeDialog',
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    balance: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      rechargeLoading: false,
      rechargeForm: {
        rechargeAmount: '',
      },
      showQrcode: false,
      qrcodeData: {},
      checkLoading: false,
      showNoPayMsg: false,
      showResult: false,
      resultMsg: '',
      isResultSucess: true,
    }
  },
  computed: {
    rechargeRules() {
      return {
        rechargeAmount: [
          {
            required: true,
            message: this.$t('financial.mall.rechargeAmountRequired'),
            trigger: ['change', 'blur'],
          },
          {
            validator: this.amountValidator,
            trigger: ['change', 'blur'],
          },
        ],
      }
    },
  },
  methods: {
    ...mapActions(['setBalance']),
    amountValidator(rule, value, cb) {
      if (isFinite(value) && value > 0) {
        cb()
      } else {
        cb(new Error(this.$t('common.enterPositiveNumber')))
      }
    },
    handleClose() {
      this.$emit('update:show', false)
    },
    handleSubmit() {
      this.$refs.rechargeForm.validate(async (valid) => {
        if (valid) {
          this.rechargeLoading = true
          try {
            const result = await financialApi.mallRecharge({
              totalAmount: this.rechargeForm.rechargeAmount * 100,
              returnUrl: 'https://b.visaai.com/pay-transfer.html',
            })
            this.rechargeLoading = false
            if (result) {
              this.handleClose()
              this.qrcodeData = result
              this.showQrcode = true
            }
          } catch (err) {
            this.rechargeLoading = false
            throw new Error(err)
          }
        }
      })
    },
    handleQrcodeClose() {
      this.showQrcode = false
      this.qrcodeData = {}
    },
    async checkPayStatus() {
      this.checkLoading = true
      try {
        const result = await financialApi.checkZMRecharge({
          outTradeNo: this.qrcodeData.outTradeNo,
        })
        this.checkLoading = false
        this.showNoPayMsg = false
        this.isResultSucess = true
        if (result) {
          switch (Number(result.status)) {
            case 100:
              this.handleQrcodeClose()
              this.resultMsg = this.$t('financial.payment.rechargeSuccess')
              this.showResult = true
              this.setBalance()
              break
            case 101:
            default:
              this.handleQrcodeClose()
              this.resultMsg = this.$t('financial.mall.rechargeFailed')
              this.isResultSucess = false
              this.showResult = true
              break
            case 102:
              this.showNoPayMsg = true
              setTimeout(() => {
                this.showNoPayMsg = false
              }, 3000)
              break
            case 103:
              this.handleQrcodeClose()
              this.resultMsg = this.$t('financial.mall.rechargeCancelled')
              this.isResultSucess = false
              this.showResult = true
              break
          }
        }
      } catch (err) {
        this.checkLoading = false
        throw new Error(err)
      }
    },
    handleResultClose() {
      this.showResult = false
      this.resultMsg = ''
      this.isResultSucess = true
    },
  },
}
</script>

<style lang="scss">
.mall-recharge-dialog {
  &__balance {
    color: $color-yellow-dark;
    font-weight: 500;
  }
  &__button {
    margin-top: 24px;
    margin-bottom: 10px;
    text-align: center;
    .el-button {
      font-size: 14px;
    }
  }
  &__qrcode {
    &-box {
      padding: 10px 0;
      text-align: center;
      position: relative;
      p {
        margin-top: 0;
      }
    }
    &-title {
      font-size: 18px;
      line-height: 25px;
      color: $color-black-87;
      margin-bottom: 8px;
    }
    &-price {
      font-size: 32px;
      line-height: 45px;
      color: $color-yellow-dark;
      margin-bottom: 0;
    }
    &-image {
      width: 200px;
    }
    &-desc {
      font-size: 14px;
      line-height: 20px;
      color: $color-black-87;
      margin-bottom: 0;
    }
    &-tips {
      position: absolute;
      bottom: 58px;
      left: 0;
      z-index: 100;
      width: 100%;
      margin: 0;
      padding: 6px 0;
      background-color: $color-black;
      color: $color-white;
      font-size: 14px;
      text-align: center;
      opacity: 0.8;
    }
    .el-button {
      margin-top: 16px;
    }
  }
  &__result {
    &-box {
      padding-bottom: 10px;
      text-align: center;
      i {
        font-size: 24px;
        display: block;
        margin-bottom: 8px;
        &.el-icon-circle-check {
          color: $color-green-light;
        }
        &.el-icon-circle-close {
          color: $color-red-light;
        }
      }
      p {
        margin-top: 0;
        margin-bottom: 24px;
        color: $color-black-87;
      }
      .el-button {
        width: 88px;
      }
    }
  }
  .el-form-item {
    &.el-form-item--small {
      margin-bottom: 6px;
    }
    &__label {
      &::before {
        display: none;
      }
      &::after {
        content: '*';
        color: $color-red-light;
        margin-left: 4px;
      }
    }
  }
}
</style>
